import { useUpdateStaffActionPermissionsMutation } from "@src/features/app/top-nav/SetActionPermissions.generated";
import { Button } from "@src/lib/ui/Button";
import Spinner from "@src/lib/ui/Spinner";
import Text from "@src/lib/ui/Text";
import { Switch } from "@src/lib/ui/Switch";
import useActionPermissions from "@src/features/app/hooks/useActionPermissions";
import useUser from "@src/features/app/hooks/useUser";
import { GraphqlOperations, UserType } from "@src/types.generated";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@src/lib/ui/Accordion";
import {
  useUserActionPermissionDetails,
  UserActionPermissionsKey,
} from "@src/features/roles/utils/actionPermissions";
import gql from "graphql-tag";
import { startCase, toLower } from "lodash";
import { FC, useCallback, useMemo } from "react";
import { useUpdateUserMutation } from "@src/features/users/user-details/UserDetails.generated";

const SetActionPermissions: FC = () => {
  const { isStaff } = useUser();
  const { userActionPermissions, loading } = useActionPermissions();
  const permissionDetails = useUserActionPermissionDetails();
  const [updateStaffPermissions] = useUpdateStaffActionPermissionsMutation({
    refetchQueries: [GraphqlOperations.Query.UserActionPermissions],
  });

  const updatePermission = useCallback(
    (field: UserActionPermissionsKey, currentValue: boolean) => {
      updateStaffPermissions({
        variables: {
          input: {
            [field]: {
              value: !currentValue,
            },
          },
        },
      });
    },
    [updateStaffPermissions],
  );
  const permissionToggleRows = useMemo(() => {
    if (!userActionPermissions) return null;
    return Object.entries(userActionPermissions).map(
      ([_permissionKey, selected]) => {
        const permissionKey = _permissionKey as UserActionPermissionsKey;
        return (
          <PermissionToggleRow
            key={permissionKey}
            title={permissionDetails[permissionKey].name}
            selected={selected}
            onUpdate={() => updatePermission(permissionKey, selected)}
          />
        );
      },
    );
  }, [userActionPermissions, updatePermission, permissionDetails]);
  if (!isStaff) {
    return null;
  }
  return (
    <div className="flex flex-col">
      <div className="mt-3 flex flex-col">
        <Text variant="body-title" style={{ marginLeft: 4 }}>
          User type
        </Text>
        <div className="flex flex-col pt-3">
          <div className="flex gap-1.5">
            <UpdateUserTypeButton userType={UserType.Admin} />
            <UpdateUserTypeButton userType={UserType.Manager} />
            <UpdateUserTypeButton userType={UserType.Employee} />
          </div>
        </div>
      </div>
      <Accordion type="single" collapsible className="w-full">
        <AccordionItem value="action-permissions">
          <AccordionTrigger>
            <Text variant="body-title">Override action permissions</Text>
          </AccordionTrigger>
          <AccordionContent>
            {loading && userActionPermissions == null ? (
              <Spinner />
            ) : (
              <div className="flex flex-col gap-1.5">
                {permissionToggleRows}
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

type PermissionToggleRowProps = {
  title: string;
  selected: boolean;
  onUpdate: () => void;
};

const PermissionToggleRow: FC<PermissionToggleRowProps> = ({
  title,
  selected,
  onUpdate,
}) => (
  <div className="flex">
    <Text className="w-[200px]" variant="compact-description" multiline>
      {title}
    </Text>
    <Switch checked={selected} onCheckedChange={() => onUpdate()} />
  </div>
);

type UpdateUserTypeButtonProps = {
  userType: UserType;
};

const UpdateUserTypeButton: FC<UpdateUserTypeButtonProps> = ({ userType }) => {
  const { user } = useUser();
  const [updateUserType, { loading: saveUserTypeLoading }] =
    useUpdateUserMutation({
      refetchQueries: [
        GraphqlOperations.Query.UserActionPermissions,
        GraphqlOperations.Query.Init,
      ],
    });
  return (
    <Button
      variant={user?.userType === userType ? "default" : "outline"}
      loading={saveUserTypeLoading}
      onClick={() => {
        if (!user?.id || !user?.userType || user.userType === userType) {
          return;
        }
        updateUserType({
          variables: {
            id: user.id,
            input: {
              userType: { value: userType },
            },
          },
        });
      }}
    >
      {startCase(toLower(userType))}
    </Button>
  );
};

export default SetActionPermissions;

gql`
  mutation updateStaffActionPermissions($input: UserActionPermissionsInput!) {
    updateUserActionPermissions(input: $input) {
      success
    }
  }
`;
